import { useState } from 'react';
import { Card, Button, Badge, useToast, Notice } from '@kollez/design-system';
import { useAdvisor, type CareerPath, type AdvisorResult } from '@/lib/queries';
import { formatLPA } from '@kollez/shared';

/**
 * /student/advisor (F69) — Nexie proposes 3 grounded career paths with
 * milestones, gaps, and salary bands from real placement data.
 *
 * Deep-audit fixes (Page 12/N):
 *   - Failures showed a transient toast that vanished after 5s; the student
 *     had no way to retry. Now surfaces inline as a Notice with retry.
 *   - Salary band label 'Salary band (25 / 50 / 75)' didn't explain what
 *     p25/p50/p75 mean. Now labels them 'Low / Typical / High' with the
 *     percentile methodology in a title tooltip.
 *   - Stamped 'Just now' on the current run so students know the advice
 *     is fresh (until reload — no persisted GET yet on the API side).
 *   - Empty-state copy shortened; kept the specificity about what Nexie
 *     grounds on.
 */
export function StudentAdvisorPage() {
  const advise = useAdvisor();
  const toast = useToast();
  const [result, setResult] = useState<AdvisorResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null);

  async function run() {
    setRunError(null);
    try {
      const r = await advise.mutateAsync();
      setResult(r.advisor);
      setGeneratedAt(new Date());
    } catch (e) {
      setRunError((e as Error)?.message ?? 'Could not fetch advice.');
      toast.show({ tone: 'warning', message: 'Could not fetch advice.' });
    }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Career advisor</p>
          <h1 className="h-page">Where should you aim?</h1>
          <p className="subhead">Nexie proposes 3 paths grounded in your profile and your college's real placement data.</p>
        </div>
        <div className="ph-actions">
          <Button variant="primary" onClick={run} loading={advise.isPending}>
            {result ? 'Refresh advice' : 'Get my advice'}
          </Button>
        </div>
      </div>

      {runError && (
        <Notice tone="warning">
          {runError}
          <div className="mt-2">
            <Button variant="secondary" onClick={run}>Try again</Button>
          </div>
        </Notice>
      )}

      {result ? (
        <>
          <Card padding="lg">
            <div className="grid gap-3 md:grid-cols-[1fr_1fr]">
              <div>
                <div className="flex items-baseline gap-2 mb-1">
                  <div className="text-xs uppercase tracking-wide text-ink-tertiary">Nexie says</div>
                  {generatedAt && <span className="text-xs text-ink-tertiary">· generated {generatedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
                </div>
                <p className="whitespace-pre-line leading-relaxed">{result.narrative}</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-ink-tertiary mb-1">Market note</div>
                <p className="whitespace-pre-line text-sm text-ink-secondary">{result.marketNote || 'Market data will refine as more offers land.'}</p>
              </div>
            </div>
          </Card>

          <div className="grid gap-3 md:grid-cols-3">
            {result.paths.map((p) => <PathCard key={p.pathName} path={p} />)}
          </div>

          {result.suggestedNextSteps.length > 0 && (
            <Card padding="lg">
              <h2 className="h-section mb-3">Next steps</h2>
              <ul className="grid gap-2 text-sm">
                {result.suggestedNextSteps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-ink-accent">→</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      ) : (
        <Card padding="lg">
          <div className="text-ink-secondary">
            Hit <b>Get my advice</b> above. Nexie reads your profile, CGPA, skills, and your college's recent
            offers, then proposes three concrete paths to aim for.
          </div>
        </Card>
      )}
    </div>
  );
}

function PathCard({ path }: { path: CareerPath }) {
  const fitTone = path.fit === 'strong' ? 'success' : path.fit === 'moderate' ? 'info' : 'outline';
  return (
    <Card padding="md">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="h-subsection">{path.pathName}</h3>
        <Badge tone={fitTone as 'success' | 'info' | 'outline'}>{path.fit} fit</Badge>
      </div>
      <div className="mb-3">
        <div
          className="text-xs uppercase tracking-wide text-ink-tertiary"
          title="Percentiles: 25% of similar offers land at or below the low band; 50% at or below typical; 75% at or below the high band."
        >
          Salary — Low · Typical · High
        </div>
        <div className="font-mono">
          {formatLPA(path.salaryBand.p25)} · <b>{formatLPA(path.salaryBand.p50)}</b> · {formatLPA(path.salaryBand.p75)}
        </div>
        <div className="text-xs text-ink-tertiary mt-1">Grounded in: {path.groundedIn.join(' · ')}</div>
      </div>

      <div className="text-xs uppercase tracking-wide text-ink-tertiary mb-1">Milestones</div>
      <ul className="grid gap-1 text-sm mb-3">
        {path.milestones.map((m, i) => (
          <li key={i} className="flex justify-between gap-2">
            <span>{m.label}</span>
            <span className="text-ink-tertiary text-xs whitespace-nowrap">{m.timeframe}</span>
          </li>
        ))}
      </ul>

      {path.gaps.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-wide text-ink-tertiary mb-1">Gaps to close</div>
          <ul className="grid gap-1 text-sm">
            {path.gaps.map((g, i) => (
              <li key={i} className="flex items-start gap-2">
                <Badge tone="warning">·</Badge>
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
